import React from "react";
import cogoToast from "cogo-toast";
import { useState, useEffect, useContext } from "react";
import {
  getScorableStaffApi,
  createOrUpdateStaffScoringApi,
  submitStaffScoringApi,
  listScoringCriteriaApi,
  listStaffFilesApi,
  downloadStaffFileApi,
  listScoringPeriodsApi,
} from "../../services/Api";
import Select from "react-select";
import { SUCCESS_RESULT } from "../../Constants";
import { FaSave, FaPaperPlane, FaSearch, FaCalendarAlt, FaTimes, FaFile, FaDownload, FaExternalLinkAlt, FaCheckCircle, FaFileAlt, FaInfoCircle, FaList, FaMousePointer, FaCheckSquare } from "react-icons/fa";
import AppContext from "../../AppContext";
import {
  Card,
  CardBody,
  CardHeader,
  Table,
  Input,
  Button,
  Row,
  Col,
  Label,
  Form,
  FormGroup,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import BtnSpinner from "../../components/BtnSpinner";
import Swal from "sweetalert2";

// Performance criteria dropdown options
const PERFORMANCE_OPTIONS = [
  { value: 5, display: "High Achiever" },
  { value: 4, display: "Achiever" },
  { value: 1, display: "Low Achiever" },
];

const StaffScoring = () => {
  const global = useContext(AppContext);
  const [user, setUser] = useState(null);
  const [is_processing, setProcessing] = useState(false);
  const [scorableStaff, setScorableStaff] = useState([]);
  const [criteriaList, setCriteriaList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [scoringData, setScoringData] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showScoringPanel, setShowScoringPanel] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [periodsList, setPeriodsList] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [currentScoringEntry, setCurrentScoringEntry] = useState(null);
  const [staffFiles, setStaffFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [canEdit, setCanEdit] = useState(true);
  const [showGuidance, setShowGuidance] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [performanceYear1, setPerformanceYear1] = useState("");
  const [performanceYear2, setPerformanceYear2] = useState("");
  const [skillsQualificationAnswer, setSkillsQualificationAnswer] = useState("");

  useEffect(() => {
    const loggedInUser = global.get_logged_in_user();
    if (loggedInUser) {
      setUser(loggedInUser);
    }
  }, [global]);

  useEffect(() => {
    if (user) {
      fetchPeriods();
      fetchScorableStaff();
    }
  }, [user]);

  useEffect(() => {
    // Check if user has seen the guidance before
    const hasSeenGuidance = localStorage.getItem('staff_scoring_guidance_seen');
    if (!hasSeenGuidance) {
      setShowGuidance(true);
    }
  }, []);

  useEffect(() => {
    if (user && selectedPeriod) {
      fetchScorableStaff();
      fetchCriteria();
    }
  }, [selectedPeriod, user]);

  // Recalculate seniority score when criteria list or staff changes
  useEffect(() => {
    if (showScoringPanel && selectedStaff && criteriaList.length > 0) {
      const seniorityCriteria = criteriaList.find(
        (c) => c.name.toLowerCase() === "seniority"
      );
      
      const joinedDate = getJoinedDate(selectedStaff);
      if (seniorityCriteria && joinedDate) {
        const seniorityScore = calculateSeniorityScore(joinedDate);
        if (seniorityScore !== "") {
          setScoringData((prev) => ({
            ...prev,
            [seniorityCriteria.id]: seniorityScore,
          }));
        }
      }
    }
  }, [criteriaList, selectedStaff, showScoringPanel]);

  // Scroll to top when panel opens
  useEffect(() => {
    if (showScoringPanel) {
      // Use multiple attempts to ensure scroll happens
      const scrollToTop = () => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        // Also try scrolling any scrollable containers
        const scrollableElements = document.querySelectorAll('[style*="overflow"], .table-responsive, .card-body, main, [role="main"]');
        scrollableElements.forEach(el => {
          if (el && el.scrollTop !== undefined) {
            el.scrollTop = 0;
          }
        });
      };
      
      // Immediate scroll
      scrollToTop();
      // Also try after a short delay
      setTimeout(scrollToTop, 50);
      setTimeout(scrollToTop, 100);
    }
  }, [showScoringPanel]);

  const fetchPeriods = () => {
    listScoringPeriodsApi({ status: 10001 })
      .then((res) => {
        if (res.status === 200) {
          const response = res.data;
          if (response.status === SUCCESS_RESULT) {
            const periods = response.data || [];
            setPeriodsList(periods);
            // Auto-select first period if available
            if (periods.length > 0 && !selectedPeriod) {
              setSelectedPeriod({
                value: periods[0].id,
                label: periods[0].name,
              });
            }
          }
        }
      })
      .catch((e) => {
        console.error("Failed to load periods:", e);
      });
  };

  const fetchScorableStaff = () => {
    if (!user || !user.id) {
      cogoToast.error("User ID not found", { hideAfter: 3 });
      return;
    }

    setProcessing(true);
    const payload = { user_id: user.id };
    if (selectedPeriod && selectedPeriod.value) {
      payload.scoring_period_id = selectedPeriod.value;
    }
    getScorableStaffApi(payload)
      .then((res) => {
        setProcessing(false);
        if (res.status === 200) {
          const response = res.data;
          if (response.status === SUCCESS_RESULT) {
            setScorableStaff(response.data || []);
          } else {
            cogoToast.error(response.message || "Failed to load scorable staff", {
              hideAfter: 5,
            });
            setScorableStaff([]);
          }
        } else {
          cogoToast.error(res.message || "Failed to load scorable staff", {
            hideAfter: 5,
          });
          setScorableStaff([]);
        }
      })
      .catch((e) => {
        setProcessing(false);
        const errorMessage =
          e?.response?.data?.message || "Something went wrong, please try again.";
        cogoToast.error(errorMessage, { hideAfter: 5 });
        setScorableStaff([]);
      });
  };

  const fetchCriteria = () => {
    const payload = selectedPeriod && selectedPeriod.value
      ? { scoring_period_id: selectedPeriod.value }
      : {};
    listScoringCriteriaApi(payload)
      .then((res) => {
        if (res.status === 200) {
          const response = res.data;
          if (response.status === SUCCESS_RESULT) {
            setCriteriaList(response.data || []);
          }
        }
      })
      .catch((e) => {
        console.error("Failed to load criteria:", e);
      });
  };

  const openScoringPanel = (staff) => {
    setSelectedStaff(staff);
    setShowScoringPanel(true);
    
    // Scroll page to top when opening panel - use immediate scroll for better reliability
    setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      // Also try scrolling any scrollable containers
      const scrollableElements = document.querySelectorAll('[style*="overflow"], .table-responsive, .card-body');
      scrollableElements.forEach(el => {
        if (el.scrollTop !== undefined) {
          el.scrollTop = 0;
        }
      });
    }, 0);
    
    // Reset performance year selections
    setPerformanceYear1("");
    setPerformanceYear2("");
    // Reset Skills & Qualifications answer
    setSkillsQualificationAnswer("");

    // Calculate and set seniority score if criteria exists
    const seniorityCriteria = criteriaList.find(
      (c) => c.name.toLowerCase() === "seniority"
    );
    
    // Check if scoring data exists
    if (staff.scoring && staff.scoring.has_scoring) {
      // Prefill with existing scoring data
      const existingScores = {};
      if (staff.scoring.details && Array.isArray(staff.scoring.details)) {
        staff.scoring.details.forEach((detail) => {
          // Handle 0 as a valid value
          const score = detail.score !== null && detail.score !== undefined 
            ? (isNaN(parseFloat(detail.score)) ? "" : parseFloat(detail.score))
            : "";
          existingScores[detail.criteria_id] = score;
        });
      }
      
    // Calculate and set seniority score if not already set or if can edit
    const joinedDate = getJoinedDate(staff);
    if (seniorityCriteria && joinedDate) {
      const seniorityScore = calculateSeniorityScore(joinedDate);
      if (seniorityScore !== "" && (!existingScores[seniorityCriteria.id] || staff.scoring.can_edit !== false)) {
        existingScores[seniorityCriteria.id] = seniorityScore;
      }
    }
    
    // Set Skills & Qualifications answer based on existing score
    const skillsCriteria = criteriaList.find(
      (c) => c.name.toLowerCase() === "skills & qualifications" || c.name.toLowerCase() === "skills and qualifications"
    );
    if (skillsCriteria && existingScores[skillsCriteria.id] !== undefined && existingScores[skillsCriteria.id] !== "") {
      const score = existingScores[skillsCriteria.id];
      setSkillsQualificationAnswer(score === 5 ? "yes" : score === 0 ? "no" : "");
    }
    
    setScoringData(existingScores);
      setRemarks(staff.scoring.remarks || "");
      setCurrentScoringEntry(staff.scoring);
      setCanEdit(staff.scoring.can_edit !== false); // Default to true if not specified
      
      // Update selected period if scoring period exists
      if (staff.scoring.scoring_period_id) {
        const period = periodsList.find((p) => p.id === staff.scoring.scoring_period_id);
        if (period) {
          setSelectedPeriod({
            value: period.id,
            label: period.name,
          });
        }
      }
    } else {
      // Initialize empty scoring data
      const initialScores = {};
      criteriaList.forEach((criteria) => {
        initialScores[criteria.id] = "";
      });
      
      // Calculate and set seniority score
      const joinedDate = getJoinedDate(staff);
      if (seniorityCriteria && joinedDate) {
        const seniorityScore = calculateSeniorityScore(joinedDate);
        if (seniorityScore !== "") {
          initialScores[seniorityCriteria.id] = seniorityScore;
        }
      }
      
      setScoringData(initialScores);
      setRemarks("");
      setCurrentScoringEntry(null);
      setCanEdit(true);
    }

    // Load staff files
    if (staff.id) {
      fetchStaffFiles(staff.id);
    }
  };

  const fetchStaffFiles = (staffId) => {
    setLoadingFiles(true);
    listStaffFilesApi({ staff_id: staffId })
      .then((res) => {
        setLoadingFiles(false);
        if (res.status === 200) {
          const response = res.data;
          if (response.status === SUCCESS_RESULT) {
            setStaffFiles(response.data || []);
          } else {
            setStaffFiles([]);
          }
        } else {
          setStaffFiles([]);
        }
      })
      .catch((e) => {
        setLoadingFiles(false);
        setStaffFiles([]);
      });
  };

  const handleDownloadFile = (file) => {
    if (!file.id) {
      cogoToast.error("File ID is missing", { hideAfter: 3 });
      return;
    }

    const loadingToast = cogoToast.loading("Opening file...", { hideAfter: 0 });

    downloadStaffFileApi({ file_id: file.id })
      .then((res) => {
        loadingToast.hide();

        const contentType = res.headers?.['content-type'] || res.data?.type || 'application/octet-stream';
        const blob = new Blob([res.data], { type: contentType });
        const url = window.URL.createObjectURL(blob);

        const newWindow = window.open(url, '_blank');

        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
          const link = document.createElement('a');
          link.href = url;
          link.download = file.file_name || file.document_name || 'download';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          cogoToast.info("Popup blocked. File downloaded instead.", { hideAfter: 3 });
        } else {
          cogoToast.success("File opened in new tab", { hideAfter: 3 });
        }

        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000);
      })
      .catch((err) => {
        loadingToast.hide();
        const errorMessage =
          err?.response?.data?.message || "Failed to open file. Please try again.";
        cogoToast.error(errorMessage, { hideAfter: 5 });
      });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const handleScoreChange = (criteriaId, value) => {
    // Handle 0 as a valid value (parseFloat(0) || "" would convert 0 to "")
    const numValue = value === "" || value === null || value === undefined 
      ? "" 
      : (isNaN(parseFloat(value)) ? "" : parseFloat(value));
    setScoringData((prev) => ({
      ...prev,
      [criteriaId]: numValue,
    }));
  };

  // Calculate performance score from year selections
  const calculatePerformanceScore = (year1, year2) => {
    if (year1 && year2) {
      const year1Value = parseFloat(year1);
      const year2Value = parseFloat(year2);
      if (!isNaN(year1Value) && !isNaN(year2Value)) {
        const average = (year1Value + year2Value) / 2;
        return average;
      }
    }
    return "";
  };

  // Helper function to get joined date from staff object (handles both Joined_at and joined_at)
  const getJoinedDate = (staff) => {
    return staff?.Joined_at || staff?.joined_at || null;
  };

  // Calculate seniority score based on years of service
  const calculateSeniorityScore = (joinedAt) => {
    if (!joinedAt) return "";
    
    try {
      const joinDate = new Date(joinedAt);
      const today = new Date();
      
      // Calculate years of service
      const yearsOfService = (today - joinDate) / (1000 * 60 * 60 * 24 * 365.25);
      
      // Assign score based on years
      if (yearsOfService >= 0 && yearsOfService < 3) {
        return 3;
      } else if (yearsOfService >= 3 && yearsOfService < 5) {
        return 4;
      } else if (yearsOfService >= 5) {
        return 5;
      }
      
      return "";
    } catch (error) {
      console.error("Error calculating seniority:", error);
      return "";
    }
  };

  // Determine certificate level based on designation
  const getCertificateLevel = (designation) => {
    if (!designation) return "Relevant certificate for the position";
    
    const designationLower = designation.toLowerCase();
    
    if (designationLower.includes("coordinator") || designationLower.includes("manager")) {
      return "Masters Degree";
    } else if (designationLower.includes("senior officer") || designationLower.includes("officer")) {
      return "Bachelors Degree";
    } else if (designationLower.includes("assistant")) {
      return "Diploma";
    } else {
      return `Relevant certificate for the position of ${designation}`;
    }
  };

  // Handle Skills & Qualifications answer
  const handleSkillsQualificationAnswer = (answer, criteriaId) => {
    setSkillsQualificationAnswer(answer);
    
    // Calculate score: Yes = 5, No = 0
    const score = answer === "yes" ? 5 : answer === "no" ? 0 : "";
    
    setScoringData((prev) => ({
      ...prev,
      [criteriaId]: score,
    }));
  };

  // Handle performance year selection changes
  const handlePerformanceYearChange = (year, yearNumber) => {
    // Update state
    let newYear1 = performanceYear1;
    let newYear2 = performanceYear2;
    
    if (yearNumber === 1) {
      newYear1 = year;
      setPerformanceYear1(year);
    } else {
      newYear2 = year;
      setPerformanceYear2(year);
    }

    // Calculate and update score using the new values
    const calculatedScore = calculatePerformanceScore(newYear1, newYear2);

    // Find Performance criteria ID
    const performanceCriteria = criteriaList.find(
      (c) => c.name.toLowerCase() === "performance"
    );

    if (performanceCriteria && calculatedScore !== "") {
      setScoringData((prev) => ({
        ...prev,
        [performanceCriteria.id]: calculatedScore,
      }));
    } else if (performanceCriteria && calculatedScore === "") {
      // Clear the score if one of the years is deselected
      setScoringData((prev) => ({
        ...prev,
        [performanceCriteria.id]: "",
      }));
    }
  };

  const validateScores = () => {
    for (const criteria of criteriaList) {
      const score = scoringData[criteria.id];
      if (score === "" || score === null || score === undefined) {
        cogoToast.error(`Please provide a score for ${criteria.name}`, {
          hideAfter: 5,
        });
        return false;
      }
      if (score < 0 || score > criteria.max_score) {
        cogoToast.error(
          `${criteria.name} score must be between 0 and ${criteria.max_score}`,
          { hideAfter: 5 }
        );
        return false;
      }
    }
    return true;
  };

  const saveScoring = (isDraft = true) => {
    if (!selectedStaff || !selectedStaff.id) {
      cogoToast.error("Staff ID is missing", { hideAfter: 3 });
      return;
    }

    if (!isDraft && !validateScores()) {
      return;
    }

    setSaving(true);

    const details = criteriaList.map((criteria) => ({
      criteria_id: criteria.id,
      score: scoringData[criteria.id] || 0,
    }));

    if (!selectedPeriod || !selectedPeriod.value) {
      cogoToast.error("Please select a scoring period", { hideAfter: 3 });
      setSaving(false);
      return;
    }

    const payload = {
      staff_id: selectedStaff.id,
      scoring_period_id: selectedPeriod.value,
      details: details,
      remarks: remarks || "",
    };

    // If updating existing entry, include the ID
    if (currentScoringEntry && currentScoringEntry.id) {
      payload.id = currentScoringEntry.id;
    }

    createOrUpdateStaffScoringApi(payload)
      .then((res) => {
        setSaving(false);
        if (res.status === 200) {
          const response = res.data;
          if (response.status === SUCCESS_RESULT) {
            setCurrentScoringEntry(response.data);
            cogoToast.success(
              isDraft
                ? response.message || "Scoring saved as draft successfully"
                : response.message || "Scoring saved successfully",
              { hideAfter: 3 }
            );
            if (!isDraft) {
              // Refresh staff list to show updated status
              fetchScorableStaff();
            }
          } else {
            cogoToast.error(response.message || "Failed to save scoring", {
              hideAfter: 5,
            });
          }
        } else {
          cogoToast.error(res.message || "Failed to save scoring", {
            hideAfter: 5,
          });
        }
      })
      .catch((e) => {
        setSaving(false);
        const errorMessage =
          e?.response?.data?.message || "Something went wrong, please try again.";
        cogoToast.error(errorMessage, { hideAfter: 5 });
      });
  };

  const submitScoring = () => {
    if (!currentScoringEntry || !currentScoringEntry.id) {
      cogoToast.error("Please save the scoring first before submitting", {
        hideAfter: 5,
      });
      return;
    }

    Swal.fire({
      title: "Submit Scoring?",
      text: `Are you sure you want to submit the scoring for ${selectedStaff?.staff_name || "this staff member"}? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, submit it",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        setSubmitting(true);
        submitStaffScoringApi({ id: currentScoringEntry.id })
          .then((res) => {
            setSubmitting(false);
            if (res.status === 200) {
              const response = res.data;
              if (response.status === SUCCESS_RESULT) {
                setShowScoringPanel(false);
                fetchScorableStaff();
                cogoToast.success(response.message || "Scoring submitted successfully", {
                  hideAfter: 3,
                });
              } else {
                cogoToast.error(response.message || "Failed to submit scoring", {
                  hideAfter: 5,
                });
              }
            } else {
              cogoToast.error(res.message || "Failed to submit scoring", {
                hideAfter: 5,
              });
            }
          })
          .catch((e) => {
            setSubmitting(false);
            const errorMessage =
              e?.response?.data?.message || "Something went wrong, please try again.";
            cogoToast.error(errorMessage, { hideAfter: 5 });
          });
      }
    });
  };

  const filteredStaff = scorableStaff.filter((staff) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      staff.staff_name?.toLowerCase().includes(searchLower) ||
      staff.staff_code?.toLowerCase().includes(searchLower) ||
      staff.email?.toLowerCase().includes(searchLower) ||
      staff.designation?.toLowerCase().includes(searchLower)
    );
  });

  // Calculate scoring progress
  const calculateProgress = () => {
    const total = scorableStaff.length;
    if (total === 0) {
      return {
        total: 0,
        submitted: 0,
        draft: 0,
        notScored: 0,
        submittedPercent: 0,
        draftPercent: 0,
        notScoredPercent: 0,
        overallPercent: 0,
      };
    }

    let submitted = 0;
    let draft = 0;
    let notScored = 0;

    scorableStaff.forEach((staff) => {
      const hasScoring = staff.scoring && staff.scoring.has_scoring;
      if (hasScoring) {
        const status = staff.scoring.status;
        if (status === 10001) {
          submitted++;
        } else if (status === 10000) {
          draft++;
        } else {
          notScored++;
        }
      } else {
        notScored++;
      }
    });

    const submittedPercent = (submitted / total) * 100;
    const draftPercent = (draft / total) * 100;
    const notScoredPercent = (notScored / total) * 100;
    const overallPercent = ((submitted + draft) / total) * 100;

    return {
      total,
      submitted,
      draft,
      notScored,
      submittedPercent,
      draftPercent,
      notScoredPercent,
      overallPercent,
    };
  };

  const progress = calculateProgress();

  const handleCloseGuidance = () => {
    if (dontShowAgain) {
      localStorage.setItem('staff_scoring_guidance_seen', 'true');
    }
    setShowGuidance(false);
  };

  return (
    <div style={{ padding: "0px" }}>
      <div className="page-header d-print-none mb-4">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <h2 className="page-title">Staff Scoring</h2>
              <div className="text-muted mt-1">
                Score staff members based on performance criteria
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container-xld">
        <Card className="shadow-sm">
          <CardHeader>
            <Row className="align-items-end">
              <Col md={8}>
                <FormGroup className="mb-0" style={{ position: "relative" }}>
                  <Label for="search" className="mb-2" style={{ fontWeight: 500, fontSize: "0.875rem", color: "#495057" }}>
                    Search Staff
                  </Label>
                  <div style={{ position: "relative" }}>
                    <FaSearch 
                      style={{
                        position: "absolute",
                        left: "0.875rem",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#6c757d",
                        fontSize: "1rem",
                        pointerEvents: "none",
                        zIndex: 1,
                      }}
                    />
                    <Input
                      type="text"
                      id="search"
                      placeholder="Search by name, code, email, or designation..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        paddingLeft: "2.5rem",
                        fontSize: "0.95rem",
                        height: "42px",
                        border: "1px solid #ced4da",
                        borderRadius: "0.375rem",
                        transition: "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#80bdff";
                        e.target.style.boxShadow = "0 0 0 0.2rem rgba(0, 123, 255, 0.25)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#ced4da";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  </div>
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup className="mb-0">
                  <Label for="scoring_period" className="mb-2" style={{ fontWeight: 500, fontSize: "0.875rem", color: "#495057" }}>
                    Scoring Period <span className="text-danger">*</span>
                  </Label>
                  <Select
                    id="scoring_period"
                    value={selectedPeriod}
                    onChange={(selected) => {
                      setSelectedPeriod(selected);
                      // Refresh staff list when period changes
                      if (selected) {
                        fetchScorableStaff();
                      }
                    }}
                    options={periodsList
                      .filter((p) => p.status === 10001)
                      .map((period) => ({
                        value: period.id,
                        label: period.name,
                      }))}
                    placeholder="Select scoring period..."
                    isSearchable
                    isClearable
                    required
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        minHeight: "42px",
                        fontSize: "0.95rem",
                        border: "1px solid #ced4da",
                      }),
                    }}
                  />
                </FormGroup>
              </Col>
            </Row>
          </CardHeader>
          
          {/* Progress Bar Section */}
          {scorableStaff.length > 0 && (
            <CardBody style={{ backgroundColor: "#f8f9fa", borderTop: "1px solid #dee2e6", borderBottom: "1px solid #dee2e6" }}>
              <Row className="align-items-center">
                <Col md={12}>
                  <div className="mb-2">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0" style={{ fontWeight: 600, color: "#495057" }}>
                        Scoring Progress
                      </h6>
                      <span style={{ fontSize: "0.875rem", color: "#6c757d", fontWeight: 500 }}>
                        {progress.submitted + progress.draft} of {progress.total} completed ({Math.round(progress.overallPercent)}%)
                      </span>
                    </div>
                    
                    {/* Overall Progress Bar */}
                    <div style={{ 
                      height: "28px", 
                      backgroundColor: "#e9ecef", 
                      borderRadius: "0.375rem",
                      overflow: "hidden",
                      position: "relative",
                      marginBottom: "12px"
                    }}>
                      {/* Submitted (Green) */}
                      {progress.submittedPercent > 0 && (
                        <div
                          style={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            width: `${progress.submittedPercent}%`,
                            height: "100%",
                            backgroundColor: "#28a745",
                            transition: "width 0.3s ease",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            zIndex: 2,
                          }}
                        >
                          {progress.submittedPercent >= 8 && `${progress.submitted} Submitted`}
                        </div>
                      )}
                      
                      {/* Draft (Yellow/Orange) */}
                      {progress.draftPercent > 0 && (
                        <div
                          style={{
                            position: "absolute",
                            left: `${progress.submittedPercent}%`,
                            top: 0,
                            width: `${progress.draftPercent}%`,
                            height: "100%",
                            backgroundColor: "#ffc107",
                            transition: "width 0.3s ease",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#000",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            zIndex: 2,
                          }}
                        >
                          {progress.draftPercent >= 8 && `${progress.draft} Draft`}
                        </div>
                      )}
                      
                      {/* Not Scored (Gray) */}
                      {progress.notScoredPercent > 0 && (
                        <div
                          style={{
                            position: "absolute",
                            left: `${progress.submittedPercent + progress.draftPercent}%`,
                            top: 0,
                            width: `${progress.notScoredPercent}%`,
                            height: "100%",
                            backgroundColor: "#6c757d",
                            transition: "width 0.3s ease",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            zIndex: 1,
                          }}
                        >
                          {progress.notScoredPercent >= 8 && `${progress.notScored} Remaining`}
                        </div>
                      )}
                    </div>
                    
                    {/* Progress Details */}
                    <Row className="mt-2">
                      <Col md={4}>
                        <div className="d-flex align-items-center">
                          <div
                            style={{
                              width: "16px",
                              height: "16px",
                              backgroundColor: "#28a745",
                              borderRadius: "4px",
                              marginRight: "8px",
                            }}
                          />
                          <div>
                            <span style={{ fontSize: "0.875rem", fontWeight: 600, color: "#495057" }}>
                              Submitted: {progress.submitted}
                            </span>
                            <span style={{ fontSize: "0.75rem", color: "#6c757d", marginLeft: "4px" }}>
                              ({Math.round(progress.submittedPercent)}%)
                            </span>
                          </div>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="d-flex align-items-center">
                          <div
                            style={{
                              width: "16px",
                              height: "16px",
                              backgroundColor: "#ffc107",
                              borderRadius: "4px",
                              marginRight: "8px",
                            }}
                          />
                          <div>
                            <span style={{ fontSize: "0.875rem", fontWeight: 600, color: "#495057" }}>
                              Draft: {progress.draft}
                            </span>
                            <span style={{ fontSize: "0.75rem", color: "#6c757d", marginLeft: "4px" }}>
                              ({Math.round(progress.draftPercent)}%)
                            </span>
                          </div>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="d-flex align-items-center">
                          <div
                            style={{
                              width: "16px",
                              height: "16px",
                              backgroundColor: "#6c757d",
                              borderRadius: "4px",
                              marginRight: "8px",
                            }}
                          />
                          <div>
                            <span style={{ fontSize: "0.875rem", fontWeight: 600, color: "#495057" }}>
                              Not Scored: {progress.notScored}
                            </span>
                            <span style={{ fontSize: "0.75rem", color: "#6c757d", marginLeft: "4px" }}>
                              ({Math.round(progress.notScoredPercent)}%)
                            </span>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>
            </CardBody>
          )}
          
          <CardBody>
            {is_processing ? (
              <div className="text-center py-5">
                <BtnSpinner loading={true} />
                <p className="mt-3 text-muted">Loading staff members...</p>
              </div>
            ) : filteredStaff.length === 0 ? (
              <div className="text-center py-5">
                <p className="text-muted">
                  {searchTerm
                    ? "No staff members found matching your search"
                    : "No staff members available for scoring"}
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover striped bordered>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Staff Code</th>
                      <th>Name</th>
                      <th>Designation</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStaff.map((staff, index) => {
                      const hasScoring = staff.scoring && staff.scoring.has_scoring;
                      const status = hasScoring ? staff.scoring.status : null;
                      const statusText = hasScoring ? staff.scoring.status_text : null;
                      const isSubmitted = status === 10001;
                      const isDraft = status === 10000;
                      
                      return (
                        <tr
                          key={staff.id}
                          onClick={() => {
                            if (!isSubmitted) {
                              openScoringPanel(staff);
                            }
                          }}
                          style={{
                            cursor: isSubmitted ? "not-allowed" : "pointer",
                          }}
                        >
                          <td>{index + 1}</td>
                          <td>
                            <strong>{staff.staff_id || "N/A"}</strong>
                          </td>
                          <td>{staff.staff_name || "N/A"}</td>
                          <td>{staff.designation || "N/A"}</td>
                          <td>{staff.email || "N/A"}</td>
                          <td onClick={(e) => e.stopPropagation()}>
                            {hasScoring ? (
                              <Badge
                                color={isSubmitted ? "success" : isDraft ? "warning" : "info"}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  padding: "6px 12px",
                                }}
                              >
                                {isSubmitted ? (
                                  <>
                                    <FaCheckCircle style={{ fontSize: "0.9rem" }} />
                                    {statusText || "Submitted"}
                                  </>
                                ) : isDraft ? (
                                  <>
                                    <FaFileAlt style={{ fontSize: "0.9rem" }} />
                                    {statusText || "Draft"}
                                  </>
                                ) : (
                                  <>
                                    <FaFileAlt style={{ fontSize: "0.9rem" }} />
                                    {statusText || "Pending"}
                                  </>
                                )}
                              </Badge>
                            ) : (
                              <Badge
                                color="secondary"
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  padding: "6px 12px",
                                }}
                              >
                                <FaFileAlt style={{ fontSize: "0.9rem" }} />
                                Not Scored
                              </Badge>
                            )}
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <Button
                              color="primary"
                              size="sm"
                              onClick={() => openScoringPanel(staff)}
                              disabled={isSubmitted}
                              style={{
                                background: isSubmitted 
                                  ? "linear-gradient(135deg, #cccccc 0%, #999999 100%)"
                                  : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                border: "none",
                                cursor: isSubmitted ? "not-allowed" : "pointer",
                                opacity: isSubmitted ? 0.6 : 1,
                              }}
                              title={isSubmitted ? "Scoring has been submitted and cannot be edited" : "Score this staff member"}
                            >
                              <FaCalendarAlt style={{ marginRight: "4px" }} />
                              Score
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Scoring Side Panel */}
      {showScoringPanel && (
        <>
          {/* Overlay */}
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 1040,
              transition: "opacity 0.3s ease",
            }}
            onClick={() => {
              setShowScoringPanel(false);
              setSelectedStaff(null);
              setScoringData({});
              setRemarks("");
              setCurrentScoringEntry(null);
              setStaffFiles([]);
              setCanEdit(true);
            }}
          />

          {/* Side Panel */}
          <div
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              width: "calc(85vw / 0.80)",
              height: "calc(100vh / 0.80)",
              maxHeight: "calc(100vh / 0.80)",
              backgroundColor: "#fff",
              zIndex: 1050,
              boxShadow: "-2px 0 10px rgba(0, 0, 0, 0.1)",
              overflowY: "auto",
              transition: "transform 0.3s ease",
              transform: showScoringPanel ? "translateX(0)" : "translateX(100%)",
            }}
          >
            <div style={{ padding: "20px" }}>
              {/* Header */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                  paddingBottom: "15px",
                  borderBottom: "2px solid #e0e0e0",
                }}
              >
                <div>
                  <h4 style={{ margin: 0, color: "#333" }}>
                    Score Staff: {selectedStaff?.staff_name} ({selectedStaff?.staff_code})
                  </h4>
                  <p style={{ margin: "5px 0 0 0", color: "#666", fontSize: "0.9rem" }}>
                    Period: {selectedPeriod ? selectedPeriod.label : "Not Selected"}
                    {currentScoringEntry && currentScoringEntry.status_text && (
                      <Badge color={currentScoringEntry.status === 10001 ? "success" : "info"} style={{ marginLeft: "10px" }}>
                        {currentScoringEntry.status_text}
                      </Badge>
                    )}
                    {currentScoringEntry && currentScoringEntry.total_score && (
                      <span style={{ marginLeft: "10px", fontWeight: "600" }}>
                        Total Score: {currentScoringEntry.total_score}
                      </span>
                    )}
                  </p>
                </div>
                <Button
                  color="secondary"
                  outline
                  onClick={() => {
                    setShowScoringPanel(false);
                    setSelectedStaff(null);
                    setScoringData({});
                    setRemarks("");
                    setCurrentScoringEntry(null);
                    setStaffFiles([]);
                  }}
                >
                  <FaTimes />
                </Button>
              </div>

              <Row>
                {/* Left Column - Scoring Form */}
                <Col md={7}>
                  <Card className="shadow-sm mb-4">
                    <CardHeader>
                      <h5 style={{ margin: 0 }}>Scoring Criteria</h5>
                    </CardHeader>
                    <CardBody>
                      <Form>
                        {criteriaList.length === 0 ? (
                          <div className="text-center py-3">
                            <p className="text-muted">
                              No scoring criteria available. Please create criteria first.
                            </p>
                          </div>
                        ) : (
                          <>
                            {criteriaList.map((criteria) => {
                              const isPerformance = criteria.name.toLowerCase() === "performance";
                              const isSeniority = criteria.name.toLowerCase() === "seniority";
                              const isSkillsQualification = criteria.name.toLowerCase() === "skills & qualifications" || criteria.name.toLowerCase() === "skills and qualifications";
                              
                              // Calculate years of service for seniority display
                              let yearsOfService = null;
                              let seniorityInfo = "";
                              let formattedHireDate = "";
                              const joinedDate = getJoinedDate(selectedStaff);
                              if (isSeniority && joinedDate) {
                                try {
                                  const joinDateObj = new Date(joinedDate);
                                  const today = new Date();
                                  yearsOfService = (today - joinDateObj) / (1000 * 60 * 60 * 24 * 365.25);
                                  seniorityInfo = `${yearsOfService.toFixed(2)} years of service`;
                                  // Format hire date for display
                                  formattedHireDate = joinDateObj.toLocaleDateString("en-US", {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                  });
                                } catch (e) {
                                  // Ignore error
                                }
                              }
                              
                              return (
                                <Card 
                                  key={criteria.id}
                                  className="mb-4"
                                  style={{
                                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                                    border: "1px solid #e0e0e0",
                                    borderRadius: "8px",
                                    overflow: "hidden",
                                    transition: "box-shadow 0.3s ease",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow = "0 6px 16px rgba(0, 0, 0, 0.15)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.1)";
                                  }}
                                >
                                  <CardBody style={{ padding: "1.25rem" }}>
                                    <FormGroup>
                                      <Label for={`score_${criteria.id}`} style={{ fontSize: "1rem", fontWeight: "600", marginBottom: "0.75rem" }}>
                                        {criteria.name} (Max: {criteria.max_score}, Weight:{" "}
                                        {criteria.weight_percentage}%)
                                        <span className="text-danger"> *</span>
                                      </Label>
                                      {criteria.description && (
                                        <div style={{ 
                                          background: "#e3f2fd", 
                                          padding: "10px 12px", 
                                          borderRadius: "6px", 
                                          marginBottom: "12px",
                                          borderLeft: "4px solid #2196f3"
                                        }}>
                                          <small style={{ color: "#1565c0", fontSize: "0.9rem", lineHeight: "1.5" }}>
                                            {criteria.description}
                                          </small>
                                        </div>
                                      )}
                                      {isSeniority && joinedDate && (
                                        <div style={{ 
                                          background: "#e8f5e9", 
                                          padding: "10px 12px", 
                                          borderRadius: "6px", 
                                          marginBottom: "12px",
                                          borderLeft: "4px solid #4caf50"
                                        }}>
                                          <small style={{ color: "#2e7d32", display: "block", fontSize: "0.9rem" }}>
                                            <strong>Auto-calculated:</strong> {seniorityInfo} (Hired: {formattedHireDate}) - {yearsOfService !== null && (yearsOfService < 3 ? "0-2.99 years = 3" : yearsOfService < 5 ? "3-4.99 years = 4" : "5+ years = 5")}
                                          </small>
                                        </div>
                                      )}
                                      {isSkillsQualification && selectedStaff?.designation && (
                                    <div style={{ 
                                      background: "#fff3e0", 
                                      padding: "14px", 
                                      borderRadius: "6px", 
                                      marginTop: "12px",
                                      marginBottom: "12px",
                                      border: "1px solid #ffb74d",
                                      borderLeft: "4px solid #ff9800"
                                    }}>
                                      <Label style={{ fontWeight: "600", marginBottom: "10px", display: "block", fontSize: "0.95rem" }}>
                                        Based on the highest certificate of this staff, does the staff have: <strong style={{ color: "#e65100" }}>{getCertificateLevel(selectedStaff.designation)}</strong>?
                                      </Label>
                                      <div style={{ display: "flex", gap: "12px" }}>
                                        <Button
                                          color={skillsQualificationAnswer === "yes" ? "success" : "outline-success"}
                                          size="sm"
                                          onClick={() => handleSkillsQualificationAnswer("yes", criteria.id)}
                                          disabled={!canEdit}
                                          style={{
                                            minWidth: "80px",
                                            fontWeight: skillsQualificationAnswer === "yes" ? "600" : "normal",
                                            background: skillsQualificationAnswer === "yes" 
                                              ? "linear-gradient(135deg, #28a745 0%, #20c997 100%)" 
                                              : undefined,
                                            border: skillsQualificationAnswer === "yes" ? "none" : undefined,
                                            color: skillsQualificationAnswer === "yes" ? "white" : undefined,
                                            boxShadow: skillsQualificationAnswer === "yes" 
                                              ? "0 2px 8px rgba(40, 167, 69, 0.3)" 
                                              : undefined
                                          }}
                                        >
                                          Yes
                                        </Button>
                                        <Button
                                          color={skillsQualificationAnswer === "no" ? "danger" : "outline-danger"}
                                          size="sm"
                                          onClick={() => handleSkillsQualificationAnswer("no", criteria.id)}
                                          disabled={!canEdit}
                                          style={{
                                            minWidth: "80px",
                                            fontWeight: skillsQualificationAnswer === "no" ? "600" : "normal"
                                          }}
                                        >
                                          No
                                        </Button>
                                      </div>
                                      {skillsQualificationAnswer && (
                                        <small style={{ display: "block", marginTop: "8px", color: "#6c757d" }}>
                                          Selected: <strong>{skillsQualificationAnswer === "yes" ? "Yes" : "No"}</strong> - Score: <strong>{scoringData[criteria.id] || 0}</strong>
                                        </small>
                                      )}
                                    </div>
                                  )}
                                  
                                  {isPerformance ? (
                                    <>
                                      <Row className="mt-2">
                                        <Col md={6}>
                                          <Label for={`performance_year1_${criteria.id}`} style={{ fontSize: "0.9rem", fontWeight: "500" }}>
                                            Year 1
                                          </Label>
                                          <Input
                                            type="select"
                                            id={`performance_year1_${criteria.id}`}
                                            value={performanceYear1}
                                            onChange={(e) => handlePerformanceYearChange(e.target.value, 1)}
                                            disabled={!canEdit}
                                            style={{ fontSize: "0.9rem" }}
                                          >
                                            <option value="">Select Year 1...</option>
                                            {PERFORMANCE_OPTIONS.map((option) => (
                                              <option key={option.value} value={option.value}>
                                                {option.display}
                                              </option>
                                            ))}
                                          </Input>
                                        </Col>
                                        <Col md={6}>
                                          <Label for={`performance_year2_${criteria.id}`} style={{ fontSize: "0.9rem", fontWeight: "500" }}>
                                            Year 2
                                          </Label>
                                          <Input
                                            type="select"
                                            id={`performance_year2_${criteria.id}`}
                                            value={performanceYear2}
                                            onChange={(e) => handlePerformanceYearChange(e.target.value, 2)}
                                            disabled={!canEdit}
                                            style={{ fontSize: "0.9rem" }}
                                          >
                                            <option value="">Select Year 2...</option>
                                            {PERFORMANCE_OPTIONS.map((option) => (
                                              <option key={option.value} value={option.value}>
                                                {option.display}
                                              </option>
                                            ))}
                                          </Input>
                                        </Col>
                                      </Row>
                                      <Input
                                        type="number"
                                        id={`score_${criteria.id}`}
                                        value={scoringData[criteria.id] !== undefined && scoringData[criteria.id] !== null ? scoringData[criteria.id] : ""}
                                        min="0"
                                        max={criteria.max_score}
                                        step="0.1"
                                        placeholder="Calculated score will appear here"
                                        required
                                        disabled={!canEdit}
                                        className="mt-2"
                                        readOnly
                                        style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                                      />
                                      {performanceYear1 && performanceYear2 && (
                                        <small className="text-muted" style={{ display: "block", marginTop: "4px" }}>
                                          Calculated: ({performanceYear1} + {performanceYear2}) / 2 = {scoringData[criteria.id] || ""}
                                        </small>
                                      )}
                                    </>
                                  ) : isSeniority ? (
                                    <Input
                                      type="number"
                                      id={`score_${criteria.id}`}
                                      value={scoringData[criteria.id] || ""}
                                      min="0"
                                      max={criteria.max_score}
                                      step="0.1"
                                      placeholder="Auto-calculated based on years of service"
                                      required
                                      disabled={!canEdit}
                                      readOnly
                                      style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                                    />
                                  ) : isSkillsQualification ? (
                                    <Input
                                      type="number"
                                      id={`score_${criteria.id}`}
                                      value={scoringData[criteria.id] !== undefined && scoringData[criteria.id] !== null ? scoringData[criteria.id] : ""}
                                      min="0"
                                      max={criteria.max_score}
                                      step="0.1"
                                      placeholder="Answer the question above to calculate score"
                                      required
                                      disabled={!canEdit}
                                      readOnly
                                      style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                                    />
                                  ) : (
                                    <Input
                                      type="number"
                                      id={`score_${criteria.id}`}
                                      value={scoringData[criteria.id] !== undefined && scoringData[criteria.id] !== null ? scoringData[criteria.id] : ""}
                                      onChange={(e) => handleScoreChange(criteria.id, e.target.value)}
                                      min="0"
                                      max={criteria.max_score}
                                      step="0.1"
                                      placeholder={`Enter score (0 - ${criteria.max_score})`}
                                      required
                                      disabled={!canEdit}
                                    />
                                  )}
                                    </FormGroup>
                                  </CardBody>
                                </Card>
                              );
                            })}

                            <FormGroup>
                              <Label for="remarks">Remarks</Label>
                              <Input
                                type="textarea"
                                id="remarks"
                                value={remarks}
                                onChange={(e) => setRemarks(e.target.value)}
                                rows="3"
                                placeholder="Enter any additional remarks or comments..."
                                disabled={!canEdit}
                              />
                            </FormGroup>
                          </>
                        )}
                      </Form>
                    </CardBody>
                  </Card>

                  {/* Action Buttons */}
                  <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
                    <Button
                      color="secondary"
                      onClick={() => {
                        setShowScoringPanel(false);
                        setSelectedStaff(null);
                        setScoringData({});
                        setRemarks("");
                        setCurrentScoringEntry(null);
                        setStaffFiles([]);
                        setPerformanceYear1("");
                        setPerformanceYear2("");
                        setSkillsQualificationAnswer("");
                      }}
                      disabled={saving || submitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      color="success"
                      onClick={() => saveScoring(false)}
                      disabled={saving || submitting || !canEdit}
                    >
                      {saving ? (
                        <>
                          <BtnSpinner loading={true} /> Saving...
                        </>
                      ) : (
                        <>
                          <FaSave style={{ marginRight: "4px" }} />
                          Save
                        </>
                      )}
                    </Button>
                    {currentScoringEntry && currentScoringEntry.id && (
                      <Button
                        color="primary"
                        onClick={submitScoring}
                        disabled={saving || submitting || !canEdit}
                        style={{
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          border: "none",
                        }}
                      >
                        {submitting ? (
                          <>
                            <BtnSpinner loading={true} /> Submitting...
                          </>
                        ) : (
                          <>
                            <FaPaperPlane style={{ marginRight: "4px" }} />
                            Submit for Approval
                          </>
                        )}
                      </Button>
                    )}
                    {!canEdit && (
                      <Badge color="warning" style={{ marginLeft: "10px", padding: "8px 12px" }}>
                        Read Only - Scoring has been submitted
                      </Badge>
                    )}
                  </div>
                </Col>

                {/* Right Column - Staff Files */}
                <Col md={5}>
                  <Card className="shadow-sm">
                    <CardHeader>
                      <h5 style={{ margin: 0 }}>Staff Files</h5>
                    </CardHeader>
                    <CardBody>
                      {loadingFiles ? (
                        <div className="text-center py-3">
                          <BtnSpinner loading={true} />
                          <p className="mt-2 text-muted" style={{ fontSize: "0.85rem" }}>
                            Loading files...
                          </p>
                        </div>
                      ) : staffFiles.length === 0 ? (
                        <div className="text-center py-4">
                          <FaFile size={48} style={{ color: "#ddd", marginBottom: "0.5rem" }} />
                          <p className="text-muted" style={{ fontSize: "0.9rem" }}>
                            No files available
                          </p>
                        </div>
                      ) : (
                        <div style={{ minHeight: "80vh", overflowY: "auto" }}>
                          {staffFiles.map((file) => (
                            <div
                              key={file.id}
                              style={{
                                padding: "12px",
                                marginBottom: "12px",
                                border: "1px solid #e0e0e0",
                                borderRadius: "6px",
                                backgroundColor: "#f9f9f9",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                }}
                              >
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: "600", marginBottom: "0.5rem" }}>
                                    {file.document_name || file.file_name || "Untitled"}
                                  </div>
                                  <div style={{ fontSize: "0.85rem", color: "#666" }}>
                                    <div>
                                      <strong>Type:</strong> {file.file_type?.name || "N/A"}
                                    </div>
                                    {file.file_size && (
                                      <div>
                                        <strong>Size:</strong> {(file.file_size / 1024).toFixed(2)} KB
                                      </div>
                                    )}
                                    {file.description && (
                                      <div style={{ marginTop: "0.25rem", fontStyle: "italic" }}>
                                        {file.description}
                                      </div>
                                    )}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "0.75rem",
                                      color: "#999",
                                      marginTop: "0.5rem",
                                      paddingTop: "0.5rem",
                                      borderTop: "1px solid #e0e0e0",
                                    }}
                                  >
                                    Uploaded: {formatDate(file.created_at)}
                                  </div>
                                </div>
                                <Button
                                  color="primary"
                                  size="sm"
                                  onClick={() => handleDownloadFile(file)}
                                  style={{
                                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                    border: "none",
                                    marginLeft: "0.5rem",
                                  }}
                                  title="Open file"
                                >
                                  <FaExternalLinkAlt />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </div>
          </div>
        </>
      )}

      {/* First-Time User Guidance Modal */}
      <Modal isOpen={showGuidance} toggle={handleCloseGuidance} size="lg" centered>
        <ModalHeader 
          toggle={handleCloseGuidance}
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            borderBottom: "none",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <FaInfoCircle style={{ fontSize: "1.5rem" }} />
            <span style={{ fontWeight: "600" }}>Welcome to Staff Scoring</span>
          </div>
        </ModalHeader>
        <ModalBody style={{ padding: "2rem" }}>
          <div style={{ marginBottom: "1.5rem" }}>
            <p style={{ fontSize: "1.1rem", color: "#495057", marginBottom: "1rem" }}>
              This guide will help you understand how to score staff members using the performance evaluation system.
            </p>
          </div>

          <div style={{ marginBottom: "1.5rem" }}>
            <h5 style={{ 
              color: "#667eea", 
              marginBottom: "1rem",
              display: "flex",
              alignItems: "center",
              gap: "8px"
            }}>
              <FaList /> Step-by-Step Guide
            </h5>
            
            <div style={{ paddingLeft: "1rem" }}>
              <div style={{ marginBottom: "1.5rem" }}>
                <div style={{ 
                  display: "flex", 
                  alignItems: "flex-start", 
                  gap: "12px",
                  marginBottom: "0.75rem"
                }}>
                  <div style={{
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    borderRadius: "50%",
                    width: "28px",
                    height: "28px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                    flexShrink: 0,
                    fontSize: "0.9rem"
                  }}>
                    1
                  </div>
                  <div>
                    <strong style={{ color: "#495057", display: "block", marginBottom: "4px" }}>
                      Select a Scoring Period
                    </strong>
                    <p style={{ color: "#6c757d", margin: 0, fontSize: "0.95rem" }}>
                      Choose the scoring period from the dropdown at the top. This determines which evaluation cycle you're working on.
                    </p>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: "1.5rem" }}>
                <div style={{ 
                  display: "flex", 
                  alignItems: "flex-start", 
                  gap: "12px",
                  marginBottom: "0.75rem"
                }}>
                  <div style={{
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    borderRadius: "50%",
                    width: "28px",
                    height: "28px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                    flexShrink: 0,
                    fontSize: "0.9rem"
                  }}>
                    2
                  </div>
                  <div>
                    <strong style={{ color: "#495057", display: "block", marginBottom: "4px" }}>
                      View Staff List
                    </strong>
                    <p style={{ color: "#6c757d", margin: 0, fontSize: "0.95rem" }}>
                      The table shows all staff members you can score. Use the search box to find specific staff members quickly.
                    </p>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: "1.5rem" }}>
                <div style={{ 
                  display: "flex", 
                  alignItems: "flex-start", 
                  gap: "12px",
                  marginBottom: "0.75rem"
                }}>
                  <div style={{
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    borderRadius: "50%",
                    width: "28px",
                    height: "28px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                    flexShrink: 0,
                    fontSize: "0.9rem"
                  }}>
                    3
                  </div>
                  <div>
                    <strong style={{ color: "#495057", display: "block", marginBottom: "4px" }}>
                      Click to Score
                    </strong>
                    <p style={{ color: "#6c757d", margin: 0, fontSize: "0.95rem" }}>
                      Click on a staff member's row or the "Score" button to open the scoring panel. You can also view their files while scoring.
                    </p>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: "1.5rem" }}>
                <div style={{ 
                  display: "flex", 
                  alignItems: "flex-start", 
                  gap: "12px",
                  marginBottom: "0.75rem"
                }}>
                  <div style={{
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    borderRadius: "50%",
                    width: "28px",
                    height: "28px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                    flexShrink: 0,
                    fontSize: "0.9rem"
                  }}>
                    4
                  </div>
                  <div>
                    <strong style={{ color: "#495057", display: "block", marginBottom: "4px" }}>
                      Enter Scores & Submit
                    </strong>
                    <p style={{ color: "#6c757d", margin: 0, fontSize: "0.95rem" }}>
                      Enter scores for each criterion based on the maximum score allowed. Add remarks if needed, then click "Submit for Approval" when done.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div style={{ 
            background: "#f8f9fa", 
            padding: "1rem", 
            borderRadius: "8px",
            marginTop: "1.5rem"
          }}>
            <h6 style={{ 
              color: "#495057", 
              marginBottom: "0.75rem",
              display: "flex",
              alignItems: "center",
              gap: "8px"
            }}>
              <FaMousePointer /> Quick Tips
            </h6>
            <ul style={{ margin: 0, paddingLeft: "1.5rem", color: "#6c757d", fontSize: "0.95rem" }}>
              <li>Status badges show: <strong>Submitted</strong> (green), <strong>Draft</strong> (yellow), or <strong>Not Scored</strong> (gray)</li>
              <li>Once submitted, scoring cannot be edited</li>
              <li>Use the progress bar to track your completion status</li>
              <li>Staff files are available in the right panel while scoring</li>
            </ul>
          </div>

          <div style={{ marginTop: "1.5rem" }}>
            <label style={{ 
              display: "flex", 
              alignItems: "center", 
              gap: "8px",
              cursor: "pointer",
              userSelect: "none"
            }}>
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                style={{ cursor: "pointer" }}
              />
              <span style={{ color: "#6c757d", fontSize: "0.95rem" }}>
                Don't show this guide again
              </span>
            </label>
          </div>
        </ModalBody>
        <ModalFooter style={{ borderTop: "none", padding: "1rem 2rem" }}>
          <Button
            color="primary"
            onClick={handleCloseGuidance}
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              border: "none",
              padding: "0.5rem 2rem",
              fontWeight: "600"
            }}
          >
            <FaCheckSquare style={{ marginRight: "8px" }} />
            Got it, let's start!
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default StaffScoring;
